import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Grid,
    IconButton,
    Alert,
    Divider,
    Fab,
    CircularProgress,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Card,
    CardContent,
    Avatar,
    Collapse,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Menu,
    Checkbox,
    FormControlLabel,
    Drawer,
    LinearProgress,
    List,
    ListItem,
    Snackbar,
} from '@mui/material';
import {
    Add,
    Close,
    Edit,
    Delete,
    People as PeopleIcon,
    CheckCircle,
    Phone,
    LocationOn,
    Person,
    Subscriptions,
    Warning,
    Contacts as ContactsIcon,
    ExpandMore,
    ExpandLess,
    GpsFixed,
    Map as MapIcon,
    EventNote as FollowUpIcon,
    Send as SendIcon,
    Call,
    CurrencyRupee,
    MoreVert,
    Search as SearchIcon,
    Clear as ClearIcon,
    ChevronRight,
    WhatsApp,
    Remove,
    Sms,
    Share as ShareIcon,
    CheckCircleOutline,
    FileDownload as DownloadIcon,
    Home as HomeIcon,
} from '@mui/icons-material';
import { businessAPI, invoiceAPI, orderAPI } from '../api';
import { normalizeMobile, isInternationalMobile, getMobileError } from '../utils/mobile';
import { contactsSupported, parseContactEntry } from '../utils/contacts';
import AreaManager from './AreaManager';
import LocationPicker from './LocationPicker';
import { CustomerPlansForCustomer, closedMonthsNote } from './CustomerPlans';
import AddPlansDialog from './AddPlansDialog';
import ShareButtons from './ShareButtons';
import InvoiceViewDialog from './InvoiceViewDialog';
import { buildInvoiceShareText } from '../utils/invoiceShareMessage';
import { makeRouteComparator, sortAreasByRoute } from '../utils/routeOrder';
import AreaFilterRow from './common/AreaFilterRow';
import { APP_DIALOG, APP_DIALOG_MAX_WIDTH, APP_DIALOG_TITLE_SX, DIALOG_TITLE_SX } from '../utils/dialogStyle';
import ScrollChipRow from './common/ScrollChipRow';

// Area filter value for "customers in no area". Area filters key on the area's name here, and
// a name is free text, so this is a value no vendor would type.
const NO_AREA_FILTER = '__no_area__';
const NO_AGENT_FILTER = '__no_agent__';

// Delivery days for a scheduled plan ("Every Sunday"), or '' for an ordinary daily product.
// Shown instead of the billing label in the pickers below, because when a plan and the product
// it is built on sit side by side, the days are the only thing telling them apart.
const planDays = (p) => (p?.schedule_label && p.schedule_label !== 'Every day' ? p.schedule_label : '');

const CustomerManager = ({ businessId, business, onCustomersUpdated, members = [], barsVisible = true, onDrawerChange, isOwner = false }) => {
    const { t } = useTranslation();
    const bizMode = business?.f_business_mode || 'subscription';
    const showSubscriptions = bizMode === 'subscription' || bizMode === 'both';
    const showOrders = bizMode === 'orders' || bizMode === 'both';
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [tagFilter, setTagFilter] = useState({ group: '', value: '' });
    const [visibleCount, setVisibleCount] = useState(50);
    const [customerTags, setCustomerTags] = useState([]);
    const [, setActiveTab] = useState(0);
    const [subscriptionsDialogOpen, setSubscriptionsDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [, setCustomerSubscriptions] = useState([]);
    const [subAddOpen, setSubAddOpen] = useState(false);
    const [subAddCustomer, setSubAddCustomer] = useState(null);
    const [products, setProducts] = useState([]);
    const [businessAreas, setBusinessAreas] = useState([]);
    const [addressExpanded, setAddressExpanded] = useState(false);
    const [gettingLocation, setGettingLocation] = useState(false);
    const [locationLabel, setLocationLabel] = useState('');
    const [locationPickerOpen, setLocationPickerOpen] = useState(false);
    const [drawerCustomerId, setDrawerCustomerId] = useState(null);
    const [drawerNotesOpen, setDrawerNotesOpen] = useState(false);
    const [notesMap, setNotesMap] = useState({});
    const [ordersMap, setOrdersMap] = useState({});  // { customerId: orders[] | null }
    const [newNoteText, setNewNoteText] = useState('');
    const [newFollowUpDate, setNewFollowUpDate] = useState('');
    const [noteLoading, setNoteLoading] = useState(false);
    const [, setBalancesMap] = useState({});
    const [subsMap, setSubsMap] = useState({});           // { customerId: [active subs] }
    const [custLastInvoice, setCustLastInvoice] = useState({}); // { customerId: invoiceListItem | null }
    const [invoicesMap, setInvoicesMap] = useState({});          // { customerId: invoice[] | undefined }
    const [invoicesVisible, setInvoicesVisible] = useState({});  // { customerId: number }
    const [ordersVisible, setOrdersVisible] = useState({});      // { customerId: number }
    const [paymentsVisible, setPaymentsVisible] = useState({});  // { customerId: number }
    const [viewInvoiceId, setViewInvoiceId] = useState(null);  // shared InvoiceViewDialog
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [payDialogCustomer, setPayDialogCustomer] = useState(null);
    const [payAmount, setPayAmount] = useState('');
    const [payDiscount, setPayDiscount] = useState('');
    const [payEditId, setPayEditId] = useState(null);  // null = new payment, number = editing existing
    const [payMethod, setPayMethod] = useState('Cash');
    const [payDate, setPayDate] = useState('');
    const [payNotes, setPayNotes] = useState('');
    const [payLoading, setPayLoading] = useState(false);
    const [paymentsMap, setPaymentsMap] = useState({});       // { customerId: [payments] }
    const [paymentsLoading, setPaymentsLoading] = useState({}); // { customerId: bool }
    const [deletePaymentOpen, setDeletePaymentOpen] = useState(false);
    const [deletePaymentTarget, setDeletePaymentTarget] = useState(null); // { paymentId, customerId }
    const [sharePayment, setSharePayment] = useState(null);   // { payment_id, customer_name, customer_mobile, amount, discount, method, time, new_balance }
    const [receiptUrl, setReceiptUrl] = useState(null);
    const [receiptLoading, setReceiptLoading] = useState(false);
    const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
    const [archiveTarget, setArchiveTarget] = useState(null); // customer id
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [newCustSubs, setNewCustSubs] = useState([]);  // [{productId, quantity}] for add-customer flow
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name');
    // Customers here carry f_area_id / f_sort_order / f_name, which is the comparator's default shape.
    const routeComparator = React.useMemo(() => makeRouteComparator(businessAreas), [businessAreas]);
    useEffect(() => { setVisibleCount(50); }, [searchQuery, tagFilter, sortBy]);
    // Reset to 'name' sort if the currently selected sort option is no longer available
    useEffect(() => {
        if (sortBy === 'followup' && !customers.some(c => c.f_follow_up_date)) setSortBy('name');
        if ((sortBy === 'last_payment_desc' || sortBy === 'last_payment_asc') && !customers.some(c => c.last_payment_date)) setSortBy('name');
        if ((sortBy === 'last_order_desc' || sortBy === 'last_order_asc') && !customers.some(c => c.last_order_date)) setSortBy('name');
    }, [customers]); // eslint-disable-line react-hooks/exhaustive-deps

    // Bulk import state
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [bulkContacts, setBulkContacts] = useState([]);
    const [bulkImporting, setBulkImporting] = useState(false);
    const [bulkDone, setBulkDone] = useState(false);

    const handlePickContact = async () => {
        try {
            const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: true });
            if (!contacts || contacts.length === 0) return;
            if (contacts.length === 1) {
                // Single — fill the add-customer form as before
                const { name, mobile } = parseContactEntry(contacts[0]);
                setFormData(prev => ({
                    ...prev,
                    f_name: name || prev.f_name,
                    f_mobile: mobile || prev.f_mobile,
                }));
            } else {
                // Multiple — open bulk import dialog
                const parsed = contacts
                    .map(c => { const { name, mobile } = parseContactEntry(c); return { name, mobile, selected: true, status: 'pending', error: '' }; })
                    .filter(c => c.name || c.mobile);
                setBulkContacts(parsed);
                setBulkDone(false);
                setBulkDialogOpen(true);
            }
        } catch (err) {
            console.error('Contact picker error:', err);
        }
    };

    const handleBulkImport = async () => {
        setBulkImporting(true);
        setBulkDone(false);
        const updated = bulkContacts.map(c => ({ ...c }));
        for (let i = 0; i < updated.length; i++) {
            if (!updated[i].selected) continue;
            if (!updated[i].name || !updated[i].mobile) {
                updated[i].status = 'error';
                updated[i].error = 'Name and mobile required';
                setBulkContacts([...updated]);
                continue;
            }
            try {
                await businessAPI.createCustomer({
                    f_business_id: businessId,
                    f_name: updated[i].name,
                    f_mobile: updated[i].mobile,
                    f_status: 'Active',
                });
                updated[i].status = 'success';
                updated[i].error = '';
            } catch (err) {
                const detail = err.response?.data?.detail;
                updated[i].status = 'error';
                updated[i].error = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : err.message || 'Failed');
            }
            setBulkContacts([...updated]);
        }
        setBulkImporting(false);
        setBulkDone(true);
        await loadCustomers();
        if (onCustomersUpdated) onCustomersUpdated();
    };

    // Customer form data
    const [formData, setFormData] = useState({
        f_mobile: '',
        f_has_mobile: true,     // false = "This customer has no phone"
        f_name: '',
        f_email: '',
        f_area_id: '',
        f_agent_id: '',
        f_address_line1: '',
        f_address_line2: '',
        f_city: '',
        f_state: '',
        f_pincode: '',
        f_latitude: '',
        f_longitude: '',
        f_status: 'Active',
        f_note: '',
        f_follow_up_date: '',
    });

    useEffect(() => {
        if (businessId) {
            loadCustomers();
            loadAreas();
            loadTags();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    const loadAreas = async () => {
        try {
            const [areasRes, productsRes] = await Promise.all([
                businessAPI.getBusinessAreas(businessId),
                // Plans included — a customer's subscription may point at "Sunday Curd". Deleted
                // ones too, so old plans keep their name; every picker filters them out.
                businessAPI.getBusinessProducts(businessId, true, true),
            ]);
            setBusinessAreas(areasRes.data || []);
            setProducts(productsRes.data || []);
        } catch (err) {
            console.error('Failed to load areas/products:', err);
        }
    };

    const loadTags = async () => {
        try {
            const res = await businessAPI.getCustomerTags(businessId);
            setCustomerTags(res.data || []);
        } catch (err) {
            console.error('Failed to load customer tags:', err);
        }
    };

    const loadNotes = useCallback(async (customerId) => {
        try {
            const res = await businessAPI.getCustomerNotes(customerId);
            setNotesMap(prev => ({ ...prev, [customerId]: res.data || [] }));
        } catch (err) {
            console.error('Failed to load notes:', err);
        }
    }, []);

    const loadCustomerSubs = useCallback(async (customerId) => {
        try {
            const res = await businessAPI.getCustomerActiveSubscriptions(customerId);
            setSubsMap(prev => ({ ...prev, [customerId]: res.data || [] }));
        } catch (err) {
            console.error('Failed to load active subscriptions:', err);
            setSubsMap(prev => ({ ...prev, [customerId]: [] }));
        }
    }, []);

    const loadCustomerLastInvoice = useCallback(async (customerId) => {
        try {
            const res = await invoiceAPI.listInvoices(businessId, { customer_id: customerId, locked_only: true });
            const invoices = res.data || [];
            setCustLastInvoice(prev => ({ ...prev, [customerId]: invoices[0] || null }));
        } catch (err) {
            setCustLastInvoice(prev => ({ ...prev, [customerId]: null }));
        }
    }, [businessId]);

    const loadCustomerInvoices = useCallback(async (customerId) => {
        setInvoicesMap(prev => ({ ...prev, [customerId]: undefined }));
        try {
            const res = await invoiceAPI.listInvoices(businessId, { customer_id: customerId, locked_only: true });
            setInvoicesMap(prev => ({ ...prev, [customerId]: res.data || [] }));
        } catch (err) {
            setInvoicesMap(prev => ({ ...prev, [customerId]: [] }));
        }
    }, [businessId]);

    const loadCustomerOrders = useCallback(async (customerId) => {
        setOrdersMap(prev => ({ ...prev, [customerId]: undefined }));
        try {
            const res = await orderAPI.listOrders(businessId, {
                customer_id: customerId, sort_by: 'updated_at', sort_order: 'desc', limit: 20,
            });
            setOrdersMap(prev => ({ ...prev, [customerId]: res.data || [] }));
        } catch (err) {
            setOrdersMap(prev => ({ ...prev, [customerId]: [] }));
        }
    }, [businessId]);

    const handleOpenDrawer = (customer) => {
        setDrawerCustomerId(customer.f_id);
        setDrawerNotesOpen(false);
        if (onDrawerChange) onDrawerChange(true);
        loadCustomerSubs(customer.f_id);
        if (custLastInvoice[customer.f_id] === undefined) loadCustomerLastInvoice(customer.f_id);
        if (showSubscriptions) loadCustomerInvoices(customer.f_id);
        loadCustomerPayments(customer.f_id);
        if (showOrders) loadCustomerOrders(customer.f_id);
    };

    const handleCloseDrawer = () => { setDrawerCustomerId(null); if (onDrawerChange) onDrawerChange(false); };

    const handleOpenPayDialog = (customer) => {
        setPayDialogCustomer(customer);
        setPayEditId(null);
        setPayAmount('');
        setPayDiscount('');
        setPayMethod('Cash');
        setPayDate(new Date().toISOString().slice(0, 10));
        setPayNotes('');
        setPayDialogOpen(true);
    };

    const handleOpenEditPayDialog = (customer, payment) => {
        setPayDialogCustomer(customer);
        setPayEditId(payment.f_id);
        setPayAmount(String(parseFloat(payment.f_amount)));
        setPayDiscount(parseFloat(payment.f_discount) > 0 ? String(parseFloat(payment.f_discount)) : '');
        setPayMethod(payment.f_payment_method);
        setPayDate(payment.f_time.slice(0, 10));
        setPayNotes(payment.f_notes || '');
        setPayDialogOpen(true);
    };

    // ── Payment Receipt Sharing ──────────────────────────────────────────
    const buildReceiptText = (sp, url) => {
        const lines = [
            `Payment Receipt`,
            ``,
            `Customer: ${sp.customer_name}`,
            `Amount Paid: Rs.${Number(sp.amount).toLocaleString('en-IN')}`,
            sp.discount > 0 ? `Discount: Rs.${Number(sp.discount).toLocaleString('en-IN')}` : null,
            `Method: ${sp.method}`,
            `Date: ${new Date(sp.time).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
            ``,
            sp.new_balance !== null
                ? (Number(sp.new_balance) > 0
                    ? `Remaining Balance: Rs.${Number(sp.new_balance).toLocaleString('en-IN')}`
                    : `Balance: Cleared`)
                : null,
            url ? `\nView Receipt: ${url}` : null,
            ``,
            `Thank you!`,
        ];
        return lines.filter(l => l !== null).join('\n');
    };

    const openShareSheet = async (sp) => {
        setReceiptUrl(null);
        setSharePayment(sp);
        setReceiptLoading(true);
        try {
            const res = await invoiceAPI.getPaymentShareUrl(businessId, sp.payment_id);
            setReceiptUrl(res.data.share_url);
        } catch (_) {
            // URL fetch failed — share without link
        } finally {
            setReceiptLoading(false);
        }
    };

    const handleReceiptShareWhatsApp = async (sp, business_wa = false) => {
        const phone = (sp.customer_mobile || '').replace(/\D/g, '');
        const waPhone = phone.length === 10 ? `91${phone}` : phone;
        const text = buildReceiptText(sp, receiptUrl);
        const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`;
        window.location.href = url;
        try { await invoiceAPI.markPaymentShared(businessId, sp.payment_id); } catch (_) {}
        setSharePayment(null);
        setReceiptUrl(null);
    };

    const handleReceiptShareSms = async (sp) => {
        const phone = (sp.customer_mobile || '').replace(/\D/g, '');
        const smsPhone = phone.length === 10 ? `+91${phone}` : phone;
        const text = buildReceiptText(sp, receiptUrl);
        window.location.href = `sms:${smsPhone}?body=${encodeURIComponent(text)}`;
        try { await invoiceAPI.markPaymentShared(businessId, sp.payment_id); } catch (_) {}
        setSharePayment(null);
        setReceiptUrl(null);
    };

    const handleSavePayment = async () => {
        const amt = parseFloat(payAmount) || 0;
        const disc = parseFloat(payDiscount) || 0;
        if (amt < 0 || (amt === 0 && disc <= 0)) return;
        setPayLoading(true);
        try {
            if (payEditId) {
                await invoiceAPI.updatePayment(businessId, payEditId, {
                    f_amount: amt,
                    f_discount: disc,
                    f_time: payDate ? new Date(payDate + 'T12:00:00').toISOString() : new Date().toISOString(),
                    f_payment_method: payMethod,
                    f_notes: payNotes || null,
                });
                setSuccess(`Payment updated for ${payDialogCustomer.f_name}`);
            } else {
                const res = await invoiceAPI.recordPayment(businessId, {
                    f_business_customer_id: payDialogCustomer.f_id,
                    f_amount: amt,
                    f_discount: disc,
                    f_time: payDate ? new Date(payDate + 'T12:00:00').toISOString() : new Date().toISOString(),
                    f_payment_method: payMethod,
                    f_notes: payNotes || null,
                    f_source: 'customers',
                });
                const savedPayment = res.data;
                openShareSheet({
                    payment_id: savedPayment.f_id,
                    customer_name: payDialogCustomer.f_name,
                    customer_mobile: payDialogCustomer.f_mobile,
                    amount: amt,
                    discount: disc,
                    method: payMethod,
                    time: new Date(),
                    new_balance: savedPayment.new_balance ?? null,
                });
            }
            setPayDialogOpen(false);
            await loadCustomers();
            loadCustomerLastInvoice(payDialogCustomer.f_id);
            loadCustomerPayments(payDialogCustomer.f_id);
            if (onCustomersUpdated) onCustomersUpdated();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not save payment.');
        } finally {
            setPayLoading(false);
        }
    };

    const loadCustomerPayments = useCallback(async (customerId) => {
        setPaymentsLoading(prev => ({ ...prev, [customerId]: true }));
        try {
            const res = await invoiceAPI.listPayments(businessId, { customer_id: customerId });
            setPaymentsMap(prev => ({ ...prev, [customerId]: res.data || [] }));
        } catch (err) {
            console.error('Failed to load payments:', err);
            setPaymentsMap(prev => ({ ...prev, [customerId]: [] }));
        } finally {
            setPaymentsLoading(prev => ({ ...prev, [customerId]: false }));
        }
    }, [businessId]);

    const handleDeletePayment = async () => {
        if (!deletePaymentTarget) return;
        const { paymentId, customerId } = deletePaymentTarget;
        try {
            await invoiceAPI.deletePayment(businessId, paymentId);
            setSuccess('Payment deleted');
            setDeletePaymentOpen(false);
            setDeletePaymentTarget(null);
            await loadCustomers();
            loadCustomerLastInvoice(customerId);
            loadCustomerPayments(customerId);
            if (onCustomersUpdated) onCustomersUpdated();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not delete payment.');
        }
    };

    const handleToggleDrawerNotes = () => {
        if (!drawerNotesOpen && drawerCustomerId && !notesMap[drawerCustomerId]) {
            loadNotes(drawerCustomerId);
        }
        setDrawerNotesOpen(prev => !prev);
        setNewNoteText('');
        setNewFollowUpDate('');
    };

    const handleAddNote = async (customerId) => {
        if (!newNoteText.trim() && !newFollowUpDate) return;
        setNoteLoading(true);
        try {
            await businessAPI.addCustomerNote(customerId, {
                f_note: newNoteText.trim() || null,
                f_follow_up_date: newFollowUpDate || null,
            });
            setNewNoteText('');
            setNewFollowUpDate('');
            await loadNotes(customerId);
            // Refresh customer list so f_note / f_follow_up_date on card updates
            await loadCustomers();
        } catch (err) {
            console.error('Failed to add note:', err);
        } finally {
            setNoteLoading(false);
        }
    };

    const loadCustomers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await businessAPI.getCustomers(businessId, false);
            setCustomers(response.data || []);
        } catch (err) {
            console.error('Failed to load customers:', err);
            setError(formatErrorMessage(err) || 'Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const formatErrorMessage = (err) => {
        if (Array.isArray(err.response?.data?.detail)) {
            return err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        }
        return err.response?.data?.detail || err.message || 'An error occurred';
    };


    const formatMobileForDisplay = (mobile) => {
        // Format mobile for display (e.g., +91 98765 43210)
        const cleaned = mobile.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
        } else if (cleaned.length > 10) {
            return `+${cleaned.slice(0, cleaned.length - 10)} ${cleaned.slice(cleaned.length - 10, cleaned.length - 5)} ${cleaned.slice(cleaned.length - 5)}`;
        }
        return mobile;
    };

    const handleOpenDialog = (customer = null) => {
        if (customer) {
            setEditingCustomer(customer);
            setFormData({
                f_mobile: customer.f_has_mobile === false ? '' : customer.f_mobile,
                f_has_mobile: customer.f_has_mobile !== false,
                f_name: customer.f_name,
                f_email: customer.f_email || '',
                f_area_id: customer.f_area_id || '',
                f_agent_id: customer.f_agent_id || '',
                f_address_line1: customer.f_address_line1 || '',
                f_address_line2: customer.f_address_line2 || '',
                f_city: customer.f_city || '',
                f_state: customer.f_state || '',
                f_pincode: customer.f_pincode || '',
                f_latitude: customer.f_latitude || '',
                f_longitude: customer.f_longitude || '',
                f_status: customer.f_status,
                f_note: customer.f_note || '',
                f_follow_up_date: customer.f_follow_up_date ? customer.f_follow_up_date.split('T')[0] : '',
            });
            // Auto-expand address section if address data exists
            const hasAddress = customer.f_address_line1 || customer.f_city || customer.f_pincode;
            setAddressExpanded(!!hasAddress);
            // Reverse geocode existing location for display
            if (customer.f_latitude && customer.f_longitude) {
                reverseGeocodeLabel(parseFloat(customer.f_latitude), parseFloat(customer.f_longitude));
            } else {
                setLocationLabel('');
            }
        } else {
            setEditingCustomer(null);
            setFormData({
                f_mobile: '',
                f_has_mobile: true,
                f_name: '',
                f_email: '',
                f_area_id: '',
                f_agent_id: '',
                f_address_line1: '',
                f_address_line2: '',
                f_city: '',
                f_state: '',
                f_pincode: '',
                f_latitude: '',
                f_longitude: '',
                f_status: 'Active',
                f_note: '',
                f_follow_up_date: '',
            });
            setAddressExpanded(false);
            setLocationLabel('');
        }
        setNewCustSubs([]);
        setActiveTab(0);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingCustomer(null);
        setNewCustSubs([]);
        setError('');
        setSuccess('');
    };

    const reverseGeocodeLabel = async (lat, lng) => {
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await res.json();
            setLocationLabel(data?.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        } catch {
            setLocationLabel(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        }
    };

    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser');
            return;
        }
        setGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setFormData(prev => ({
                    ...prev,
                    f_latitude: latitude.toFixed(6),
                    f_longitude: longitude.toFixed(6),
                }));
                reverseGeocodeLabel(latitude, longitude);
                setGettingLocation(false);
            },
            (err) => {
                setError('Could not get location: ' + err.message);
                setGettingLocation(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleSaveCustomer = async () => {
        const noPhone = formData.f_has_mobile === false;
        if (!formData.f_name || (!noPhone && !formData.f_mobile)) {
            setError(noPhone ? 'Name is required'
                : "Name and mobile number are required — or tick 'This customer has no phone'");
            return;
        }
        const mobileErr = noPhone ? '' : getMobileError(formData.f_mobile);
        if (mobileErr) {
            setError(mobileErr);
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Normalize mobile before sending
            const dataToSend = {
                ...formData,
                f_mobile: noPhone ? null : normalizeMobile(formData.f_mobile),
                f_has_mobile: !noPhone,
                f_area_id: formData.f_area_id || null,
                f_agent_id: formData.f_agent_id || null,
                f_note: formData.f_note || null,
                f_follow_up_date: formData.f_follow_up_date || null,
                f_latitude: formData.f_latitude !== '' ? parseFloat(formData.f_latitude) : null,
                f_longitude: formData.f_longitude !== '' ? parseFloat(formData.f_longitude) : null,
            };

            if (editingCustomer) {
                await businessAPI.updateCustomer(editingCustomer.f_id, dataToSend);
                setSuccess('Customer updated successfully!');
            } else {
                const res = await businessAPI.createCustomer({ ...dataToSend, f_business_id: businessId });
                const newCustId = res.data?.f_id;

                // Create subscriptions if any were selected
                if (newCustId && newCustSubs.length > 0) {
                    const userId = parseInt(localStorage.getItem('user_id'));
                    const subErrors = [];
                    for (const sub of newCustSubs) {
                        try {
                            await businessAPI.createCustomerSubscription({
                                f_business_id: businessId,
                                f_business_customer_id: newCustId,
                                f_business_product_id: sub.productId,
                                f_quantity: sub.quantity,
                                f_effective_from_date: sub.effectiveFrom,
                                f_updated_by: userId,
                                f_action: 'Start',
                                f_reason: null,
                            });
                        } catch (subErr) {
                            const detail = subErr.response?.data?.detail;
                            subErrors.push(typeof detail === 'string' ? detail : subErr.message);
                        }
                    }
                    if (subErrors.length > 0) {
                        setSuccess(`Customer created! But ${subErrors.length} plan(s) failed.`);
                    } else {
                        setSuccess(`Customer created with ${newCustSubs.length} plan(s)!`);
                    }
                } else {
                    setSuccess('Customer created successfully!');
                }
            }

            await loadCustomers();
            await loadTags();

            if (onCustomersUpdated) onCustomersUpdated();
            setTimeout(() => { handleCloseDialog(); }, 1500);
        } catch (err) {
            setError(formatErrorMessage(err) || 'Failed to save customer');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCustomer = async (customerId) => {
        setArchiveTarget(customerId);
        setArchiveConfirmOpen(true);
    };

    const confirmArchiveCustomer = async () => {
        if (!archiveTarget) return;
        setArchiveLoading(true);

        try {
            const res = await businessAPI.deleteCustomer(archiveTarget);
            const data = res.data || {};
            let msg = 'Customer archived successfully!';
            if (data.ended_subscriptions > 0) {
                msg += ` ${data.ended_subscriptions} plan(s) ended.`;
            }
            setSnackbar({ open: true, message: msg, severity: 'success' });
            setArchiveConfirmOpen(false);
            setArchiveTarget(null);
            setDrawerCustomerId(null);
            if (onDrawerChange) onDrawerChange(false);
            await loadCustomers();
            if (onCustomersUpdated) onCustomersUpdated();
        } catch (err) {
            const detail = err.response?.data?.detail;
            let errMsg;
            if (err.response?.status === 409 && detail?.conflicting_products) {
                errMsg = `${detail.message} Products: ${detail.conflicting_products.join(', ')}`;
            } else {
                errMsg = formatErrorMessage(err) || 'Failed to archive customer';
            }
            setSnackbar({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setArchiveLoading(false);
            setArchiveConfirmOpen(false);
            setArchiveTarget(null);
        }
    };

    const handleManageSubscriptions = (customer) => {
        setSelectedCustomer(customer);
        setSubscriptionsDialogOpen(true);
    };

    // Add plans: the same picker as the Plans page (start date, then every product by category).
    const handleOpenSubAdd = (customer) => {
        setSubAddCustomer(customer);
        setSubAddOpen(true);
    };
    const handleSubAdded = (affected, keepOpen) => {
        if (subAddCustomer) loadCustomerSubs(subAddCustomer.f_id);
        loadTags();   // the card's Active / Paused chip comes from the tags
        if (!keepOpen) {
            setSubAddOpen(false);
            setSuccess('Plans added.' + closedMonthsNote(affected));
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getAvatarColor = (name) => {
        const colors = ['#2563EB', '#388e3c', '#d32f2f', '#f57c00', '#7b1fa2', '#0097a7'];
        const index = name ? name.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const statusTagColor = (tag) => {
        switch (tag) {
            case 'New':      return 'info';
            case 'Active':   return 'success';
            case 'Paused':   return 'warning';
            case 'Inactive': return 'default';
            default:         return 'default';
        }
    };

    // Derive tag maps from view data
    const statusTagMap = {};
    const areaTagMap = {};
    customerTags.forEach(t => {
        if (t.tag_group === 'Status') statusTagMap[t.f_business_customer_id] = t.tag_name;
        if (t.tag_group === 'Area')   areaTagMap[t.f_business_customer_id]   = t.tag_name;
    });
    const drawerCustomer = customers.find(c => c.f_id === drawerCustomerId) || null;

    const handleDownloadCustomers = async () => {
        let plansByCustomer = {};
        try {
            const res = await businessAPI.getCustomerSubscriptions(businessId, null, null, 'Active');
            (res.data || []).forEach(log => {
                const product = products.find(p => p.f_id === log.f_business_product_id);
                const planName = product?.f_name || `Plan #${log.f_business_product_id}`;
                if (!plansByCustomer[log.f_business_customer_id]) plansByCustomer[log.f_business_customer_id] = [];
                plansByCustomer[log.f_business_customer_id].push(planName);
            });
        } catch (_) {}

        const rows = [['Name', 'Phone', 'Door No', 'Area', 'Active Plans', 'Dues (₹)', 'Status']];
        customers.forEach(c => {
            const area = areaTagMap[c.f_id] || businessAreas.find(a => a.f_id === c.f_area_id)?.f_name || '';
            const plans = (plansByCustomer[c.f_id] || []).join('; ');
            rows.push([
                c.f_name || '',
                c.f_mobile || '',
                c.f_address_line1 || '',
                area,
                plans,
                c.outstanding_balance != null ? parseFloat(c.outstanding_balance).toFixed(2) : '',
                statusTagMap[c.f_id] || '',
            ]);
        });
        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {/* Customer List - Main View */}
            <Box sx={{ pb: '90px' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Search + Sort row */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder={t('customer.search')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            flex: '0 0 66%',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                bgcolor: 'white',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                border: '1.5px solid',
                                borderColor: searchQuery ? 'primary.main' : 'grey.200',
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                '& fieldset': { border: 'none' },
                                '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                                '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: searchQuery ? 'primary.main' : 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <FormControl size="small" sx={{ flexShrink: 0, width: 'calc(34% - 8px)' }}>
                        <Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            displayEmpty
                            renderValue={(v) => {
                                const labels = {
                                    name: 'Name', area: 'Area',
                                    dues_desc: 'Dues ↓', dues_asc: 'Dues ↑',
                                    signup_desc: 'Added ↓', signup_asc: 'Added ↑',
                                    followup: 'Follow-up',
                                    last_payment_desc: 'Payment ↓', last_payment_asc: 'Payment ↑',
                                    last_order_desc: 'Order ↓', last_order_asc: 'Order ↑',
                                };
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{labels[v] || v}</Typography>
                                    </Box>
                                );
                            }}
                            sx={{
                                borderRadius: 3,
                                bgcolor: 'white',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                border: '1.5px solid',
                                borderColor: sortBy !== 'name' ? 'primary.main' : 'grey.200',
                                '& fieldset': { border: 'none' },
                                '&:hover': { borderColor: 'primary.light' },
                                '& .MuiSelect-select': { py: '6px' },
                            }}
                        >
                            <MenuItem value="name">{t('customer.sort.name')}</MenuItem>
                            <MenuItem value="route">{t('customer.sort.route')}</MenuItem>
                            <MenuItem value="area">{t('customer.sort.area')}</MenuItem>
                            <MenuItem value="dues_desc">{t('customer.sort.duesDesc')}</MenuItem>
                            <MenuItem value="dues_asc">{t('customer.sort.duesAsc')}</MenuItem>
                            <MenuItem value="signup_desc">Added ↓ (Newest)</MenuItem>
                            <MenuItem value="signup_asc">Added ↑ (Oldest)</MenuItem>
                            {customers.some(c => c.f_follow_up_date) && (
                                <MenuItem value="followup">{t('customer.sort.followup')}</MenuItem>
                            )}
                            {customers.some(c => c.last_payment_date) && [
                                <MenuItem key="last_payment_desc" value="last_payment_desc">Last Payment ↓ (Newest)</MenuItem>,
                                <MenuItem key="last_payment_asc" value="last_payment_asc">Last Payment ↑ (Oldest)</MenuItem>,
                            ]}
                            {customers.some(c => c.last_order_date) && [
                                <MenuItem key="last_order_desc" value="last_order_desc">Last Order ↓ (Newest)</MenuItem>,
                                <MenuItem key="last_order_asc" value="last_order_asc">Last Order ↑ (Oldest)</MenuItem>,
                            ]}
                        </Select>
                    </FormControl>
                </Box>

                {/* Filter chips */}
                <Box sx={{ mb: 2 }}>
                    {/* Statuses on one line, agents on the next — each scrolls sideways instead of
                        wrapping, so a business with many agents still gets one tidy row. */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <ScrollChipRow sx={{ flex: 1, minWidth: 0 }}>
                            <Chip
                                label={`All (${customers.length})`}
                                size="small"
                                color={!tagFilter.group ? 'primary' : 'default'}
                                onClick={() => setTagFilter({ group: '', value: '' })}
                                clickable
                            />
                            {['New', 'Active', 'Paused', 'Inactive'].map(tag => {
                                const count = customers.filter(c => statusTagMap[c.f_id] === tag).length;
                                if (count === 0) return null;
                                const isActive = tagFilter.group === 'Status' && tagFilter.value === tag;
                                return (
                                    <Chip
                                        key={tag}
                                        label={`${tag} (${count})`}
                                        size="small"
                                        color={isActive ? statusTagColor(tag) : 'default'}
                                        variant={isActive ? 'filled' : 'outlined'}
                                        onClick={() => setTagFilter(prev =>
                                            prev.group === 'Status' && prev.value === tag
                                                ? { group: '', value: '' }
                                                : { group: 'Status', value: tag }
                                        )}
                                        clickable
                                    />
                                );
                            })}
                            {/* Customers with no phone — find them to add a number, or to know whose
                                bills must be printed rather than sent on WhatsApp */}
                            {(() => {
                                const n = customers.filter(c => c.f_has_mobile === false).length;
                                if (!n) return null;
                                const on = tagFilter.group === 'Phone';
                                return (
                                    <Chip label={`No phone (${n})`} size="small" clickable
                                        color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                                        onClick={() => setTagFilter(on ? { group: '', value: '' } : { group: 'Phone', value: 'none' })} />
                                );
                            })()}
                        </ScrollChipRow>
                        {isOwner && (
                            <Tooltip title="Download customer list (CSV)">
                                <IconButton size="small" onClick={handleDownloadCustomers} sx={{ flexShrink: 0, ml: 0.5 }}>
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    {/* Agent chips, keyed on the member row id (names are not unique, and an agent
                        can be renamed). Every agent with customers gets one, the owner too (a
                        vendor can assign customers to themselves — before, those customers were
                        in no chip at all), plus "No agent". Their own line, apart from the
                        statuses; hidden when nobody has an agent. */}
                    {(() => {
                        const countFor = (id) => customers.filter(c => c.f_agent_id === id).length;
                        const options = (members || []).filter(m => m.f_is_active).map(m => ({
                            key: m.f_id,
                            label: m.f_role === 'Owner' ? `${m.f_name || m.f_mobile} (owner)` : (m.f_name || m.f_mobile),
                        })).map(o => ({ ...o, count: countFor(o.key) })).filter(o => o.count > 0);
                        const none = customers.filter(c => !c.f_agent_id).length;
                        if (!options.length) return null;
                        if (none) options.push({ key: NO_AGENT_FILTER, label: 'No agent', count: none });

                        return (
                            <>
                                <ScrollChipRow sx={{ mt: 1.25 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Agents</Typography>
                                    {options.map(o => {
                                        const isActive = tagFilter.group === 'Agent' && tagFilter.value === o.key;
                                        return (
                                            <Chip
                                                key={`agent-${o.key}`}
                                                label={`${o.label} (${o.count})`}
                                                size="small"
                                                color={isActive ? 'primary' : 'default'}
                                                variant={isActive ? 'filled' : 'outlined'}
                                                onClick={() => setTagFilter(prev =>
                                                    prev.group === 'Agent' && prev.value === o.key
                                                        ? { group: '', value: '' }
                                                        : { group: 'Agent', value: o.key }
                                                )}
                                                clickable
                                            />
                                        );
                                    })}
                                </ScrollChipRow>
                            </>
                        );
                    })()}
                </Box>

                {/* Areas on one line: the busiest two as chips, the rest (and No area) in a list.
                    With 20+ areas the chips used to wrap onto seven rows above the list. */}
                <AreaFilterRow
                    options={sortAreasByRoute(businessAreas)
                        .map(a => ({
                            key: a.f_name,
                            label: a.f_name,
                            count: customers.filter(c => areaTagMap[c.f_id] === a.f_name).length,
                        }))
                        .filter(a => a.count > 0)}
                    noArea={{ key: NO_AREA_FILTER, count: customers.filter(c => !areaTagMap[c.f_id]).length }}
                    total={customers.length}
                    value={tagFilter.group === 'Area' ? tagFilter.value : ''}
                    onChange={key => setTagFilter(key ? { group: 'Area', value: key } : { group: '', value: '' })}
                    sx={{ mt: -1, mb: 2 }}
                />

                {/* Sort chips REMOVED — sort moved to dropdown above */}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={60} />
                    </Box>
                ) : (() => {
                    const q = searchQuery.trim().toLowerCase();
                    const filteredCustomers = customers
                        .filter(c => {
                            if (!tagFilter.group) return true;
                            if (tagFilter.group === 'Status') return statusTagMap[c.f_id] === tagFilter.value;
                            if (tagFilter.group === 'Phone')  return c.f_has_mobile === false;
                            if (tagFilter.group === 'Area')   return tagFilter.value === NO_AREA_FILTER
                                ? !areaTagMap[c.f_id]
                                : areaTagMap[c.f_id] === tagFilter.value;
                            // Keyed on the member row id, not the agent's name: two agents
                            // can share a name, and the v_customer_tags Agent group is by name.
                            if (tagFilter.group === 'Agent')  return tagFilter.value === NO_AGENT_FILTER
                                ? !c.f_agent_id
                                : c.f_agent_id === tagFilter.value;
                            return true;
                        })
                        .filter(c => !q || [
                            c.f_name,
                            c.f_mobile,
                            c.f_address_line1,
                            businessAreas.find(a => a.f_id === c.f_area_id)?.f_name,
                        ].some(v => v && v.toLowerCase().includes(q)))
                        .sort((a, b) => {
                            // Delivery route order — the order the round actually visits houses.
                            if (sortBy === 'route') return routeComparator(a, b);
                            if (sortBy === 'dues_desc' || sortBy === 'dues_asc') {
                                const eff = (c) => c.outstanding_balance > 0 ? c.outstanding_balance : -(c.credit_balance ?? 0);
                                return sortBy === 'dues_desc' ? eff(b) - eff(a) : eff(a) - eff(b);
                            }
                            if (sortBy === 'area') {
                                const an = businessAreas.find(x => x.f_id === a.f_area_id)?.f_name || 'zzz';
                                const bn = businessAreas.find(x => x.f_id === b.f_area_id)?.f_name || 'zzz';
                                return an.localeCompare(bn);
                            }
                            if (sortBy === 'followup') {
                                const ad = a.f_follow_up_date || '9999';
                                const bd = b.f_follow_up_date || '9999';
                                return ad.localeCompare(bd);
                            }
                            if (sortBy === 'last_payment_desc') {
                                const ad = a.last_payment_date || '0000';
                                const bd = b.last_payment_date || '0000';
                                return bd.localeCompare(ad); // most recent first
                            }
                            if (sortBy === 'last_payment_asc') {
                                const ad = a.last_payment_date || '9999';
                                const bd = b.last_payment_date || '9999';
                                return ad.localeCompare(bd); // oldest first, no-payment last
                            }
                            if (sortBy === 'last_order_desc') {
                                const ad = a.last_order_date || '0000';
                                const bd = b.last_order_date || '0000';
                                return bd.localeCompare(ad);
                            }
                            if (sortBy === 'last_order_asc') {
                                const ad = a.last_order_date || '9999';
                                const bd = b.last_order_date || '9999';
                                return ad.localeCompare(bd);
                            }
                            if (sortBy === 'signup_desc') {
                                const ad = a.f_inserted_at || '0000';
                                const bd = b.f_inserted_at || '0000';
                                return bd.localeCompare(ad);
                            }
                            if (sortBy === 'signup_asc') {
                                const ad = a.f_inserted_at || '9999';
                                const bd = b.f_inserted_at || '9999';
                                return ad.localeCompare(bd);
                            }
                            // default: name
                            return (a.f_name || '').localeCompare(b.f_name || '');
                        });

                    const visibleCustomers = filteredCustomers.slice(0, visibleCount);
                    const hasMore = filteredCustomers.length > visibleCount;

                    if (filteredCustomers.length === 0) return (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1.5 }} />
                            {tagFilter.group ? (
                                <>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {t('customer.noCustomersFound')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('customer.noCustomersFilter')}
                                    </Typography>
                                </>
                            ) : (
                                <Box sx={{ maxWidth: 320, mx: 'auto', textAlign: 'left' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                                        ఇంకా కస్టమర్లు లేరు. దయచేసి కింద చదవండి.<br/>
                                        <Typography variant="caption" color="text.disabled">No customers yet. See below to get started.</Typography>
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1.5 }}>
                                            <Typography sx={{ fontSize: 20, lineHeight: 1.2, flexShrink: 0 }}>📱</Typography>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    <strong>ఫోన్ నుండి ఇంపోర్ట్ చేయండి</strong> — <em>Add Customer</em> నొక్కి <em>Fill from Phone Contacts</em> బటన్ తో వేగంగా యాడ్ చేయండి.<br/>
                                                    <Typography variant="caption" color="text.disabled">Fastest way: tap <strong>Add Customer</strong> → <strong>Fill from Phone Contacts</strong> to import name &amp; number instantly.</Typography>
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <Typography variant="caption" sx={{ minWidth: 20 }}>✍️</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>మాన్యువల్‌గా యాడ్ చేయండి</strong> — పేరు మరియు మొబైల్ నంబర్ ఇవ్వండి.<br/>
                                                <Typography variant="caption" color="text.disabled">Or add manually — enter name &amp; mobile number.</Typography>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <Typography variant="caption" sx={{ minWidth: 20 }}>🗂️</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>ఏరియా / ఏజెంట్</strong> — సెట్టింగ్స్ లో యాడ్ చేయవచ్చు, తర్వాత కస్టమర్‌కి లింక్ అవుతాయి.<br/>
                                                <Typography variant="caption" color="text.disabled">Areas &amp; agents can be set up in Settings and linked to customers.</Typography>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );

                    return (
                        <Box>
                            {visibleCustomers.map((customer) => (
                                <Card key={customer.f_id} elevation={2} sx={{ mb: 1.5, borderRadius: 2, cursor: 'pointer' }} onClick={() => handleOpenDrawer(customer)}>
                                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: getAvatarColor(customer.f_name), width: 44, height: 44, fontWeight: 'bold', flexShrink: 0, fontSize: 16 }}>
                                                {getInitials(customer.f_name)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography fontWeight="bold" noWrap sx={{ fontSize: '0.95rem' }}>{customer.f_name}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
                                                    <Phone sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }} />
                                                    <Typography variant="caption" color={customer.f_has_mobile === false ? 'text.disabled' : 'text.secondary'} noWrap>
                                                        {customer.f_has_mobile === false ? 'No phone' : formatMobileForDisplay(customer.f_mobile)}
                                                    </Typography>
                                                </Box>
                                                {(customer.f_address_line1 || areaTagMap[customer.f_id]) && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                                                        {customer.f_address_line1 && (
                                                            <>
                                                                <HomeIcon sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }} />
                                                                <Typography variant="caption" color="text.disabled" noWrap>
                                                                    {customer.f_address_line1}
                                                                </Typography>
                                                            </>
                                                        )}
                                                        {areaTagMap[customer.f_id] && (
                                                            <Typography variant="caption" color="primary.main" noWrap sx={{ fontWeight: 500 }}>
                                                                {customer.f_address_line1 ? ` · ${areaTagMap[customer.f_id]}` : areaTagMap[customer.f_id]}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Right-side context pill */}
                                            <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 64 }}>
                                                {sortBy === 'area' ? (
                                                    customer.f_area_name ? (
                                                        <Typography variant="caption" color="primary.main" fontWeight="medium" noWrap>
                                                            {customer.f_area_name}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                                    )
                                                ) : sortBy === 'followup' ? (
                                                    customer.f_follow_up_date ? (
                                                        <>
                                                            <Typography variant="caption" color="warning.main" fontWeight="bold" lineHeight={1.2} display="block">
                                                                {new Date(customer.f_follow_up_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                            </Typography>
                                                            <Typography variant="caption" color="warning.main" lineHeight={1.2} display="block">
                                                                {t('customer.card.followup')}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">{t('customer.card.noDate')}</Typography>
                                                    )
                                                ) : (sortBy === 'last_payment_desc' || sortBy === 'last_payment_asc') ? (
                                                    customer.last_payment_date ? (
                                                        <>
                                                            <Typography variant="caption" color="info.main" fontWeight="bold" lineHeight={1.2} display="block">
                                                                {new Date(customer.last_payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                            </Typography>
                                                            <Typography variant="caption" color="info.main" lineHeight={1.2} display="block">
                                                                Last paid
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">No payment</Typography>
                                                    )
                                                ) : (
                                                    customer.outstanding_balance != null ? (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold" lineHeight={1.2}
                                                                color={customer.outstanding_balance > 0 ? 'error.main' : customer.credit_balance > 0 ? 'success.main' : 'success.main'}>
                                                                {customer.credit_balance > 0 ? `+₹${customer.credit_balance.toFixed(2)}` : `₹${customer.outstanding_balance.toFixed(2)}`}
                                                            </Typography>
                                                            <Typography variant="caption" lineHeight={1.2}
                                                                color={customer.outstanding_balance > 0 ? 'error.main' : 'success.main'}>
                                                                {customer.outstanding_balance > 0 ? t('customer.card.due') : customer.credit_balance > 0 ? 'Overpaid' : t('customer.card.paidUp')}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Chip
                                                            label={statusTagMap[customer.f_id] || '—'}
                                                            size="small"
                                                            color={statusTagColor(statusTagMap[customer.f_id])}
                                                        />
                                                    )
                                                )}
                                            </Box>

                                            <ChevronRight sx={{ color: 'text.disabled', flexShrink: 0, fontSize: 20 }} />
                                        </Box>
                                    </CardContent>

                                </Card>
                            ))}
                            {hasMore && (
                                <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                                    <Button variant="outlined" size="small" onClick={() => setVisibleCount(v => v + 50)}>
                                        Load more ({filteredCustomers.length - visibleCount} remaining)
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    );
                })()}
            </Box>

            {/* Floating Add button */}
            <Fab
                color="primary"
                variant="extended"
                size="medium"
                aria-label="add customer"
                onClick={() => handleOpenDialog()}
                sx={{ position: 'fixed', bottom: barsVisible ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 'calc(16px + env(safe-area-inset-bottom, 0px))', right: 'max(16px, calc(50% - 344px))', zIndex: 1050, gap: 0.75, px: 2 }}
            >
                <Add />
                Add Customer
            </Fab>

            {/* Add/Edit Customer Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} {...APP_DIALOG}>
                <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person />
                            <Typography variant="h6">
                                {editingCustomer ? t('customer.editDetails') : t('customer.addNew')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ mt: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}

                    {/* Import from Contacts — prominent button for new customer only */}
                    {!editingCustomer && (
                        <Button
                            fullWidth
                            variant={contactsSupported ? 'contained' : 'outlined'}
                            color="success"
                            size="large"
                            startIcon={<ContactsIcon />}
                            onClick={contactsSupported ? handlePickContact : undefined}
                            disabled={!contactsSupported}
                            sx={{ mb: 2, py: 1.5, fontSize: '1rem', fontWeight: 'bold', borderRadius: 2 }}
                        >
                            {contactsSupported ? '📱 Fill from Phone Contacts' : 'Contact import not supported (use Android Chrome)'}
                        </Button>
                    )}

                    <Box>
                        <Grid container spacing={2}>
                            {/* Mobile */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={formData.f_has_mobile === false ? 'Mobile number' : t('customer.fields.mobileRequired')}
                                    value={formData.f_has_mobile === false ? '' : formData.f_mobile}
                                    disabled={formData.f_has_mobile === false}
                                    onChange={(e) => setFormData({ ...formData, f_mobile: e.target.value })}
                                    placeholder={formData.f_has_mobile === false ? 'No phone' : t('customer.fields.mobilePlaceholder')}
                                    helperText={formData.f_has_mobile === false ? 'Bills for this customer are printed or handed over — not sent on WhatsApp'
                                        : getMobileError(formData.f_mobile) || (() => {
                                        const norm = normalizeMobile(formData.f_mobile);
                                        return norm && norm !== (formData.f_mobile || '').replace(/\D/g, '') ? `Will be saved as ${norm}` : t('customer.fields.mobileHelper');
                                    })()}
                                    error={formData.f_has_mobile !== false && !!getMobileError(formData.f_mobile)}
                                    type="tel"
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    size="small"
                                />
                                {/* Some customers (often older people on a round) have no phone at all */}
                                <FormControlLabel
                                    sx={{ mt: 0.25, minHeight: 44, mr: 0 }}
                                    control={
                                        <Checkbox size="small" checked={formData.f_has_mobile === false}
                                            onChange={(e) => setFormData({ ...formData, f_has_mobile: !e.target.checked })} />
                                    }
                                    label={<Typography variant="body2">This customer has no phone</Typography>}
                                />
                            </Grid>

                            {/* Name */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t('customer.fields.nameRequired')}
                                    value={formData.f_name}
                                    onChange={(e) => setFormData({ ...formData, f_name: e.target.value })}
                                    placeholder={t('customer.fields.namePlaceholder')}
                                    size="small"
                                />
                            </Grid>

                            {/* Area — show when areas exist */}
                            {businessAreas.length > 0 && (
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t('customer.fields.area')}</InputLabel>
                                        <Select
                                            value={formData.f_area_id}
                                            onChange={(e) => setFormData({ ...formData, f_area_id: e.target.value })}
                                            label={t('customer.fields.area')}
                                        >
                                            <MenuItem value=""><em>{t('customer.fields.none')}</em></MenuItem>
                                            {businessAreas.map((area) => (
                                                <MenuItem key={area.f_id} value={area.f_id}>
                                                    {area.f_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Assigned Agent — the value is the member row f_id. Owner is left out:
                                blank already means "the owner handles this customer". */}
                            {members.filter(m => m.f_role !== 'Owner').length > 0 && (
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t('customer.fields.assignedAgent')}</InputLabel>
                                        <Select
                                            value={formData.f_agent_id}
                                            onChange={(e) => setFormData({ ...formData, f_agent_id: e.target.value })}
                                            label={t('customer.fields.assignedAgent')}
                                        >
                                            <MenuItem value=""><em>{t('customer.fields.none')}</em></MenuItem>
                                            {members.filter(m => m.f_role !== 'Owner').map((m) => (
                                                <MenuItem key={m.f_id} value={m.f_id}>
                                                    {m.f_name || m.f_mobile} ({m.f_role})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Flat No / Door No - always visible */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('customer.fields.addressLine1')}
                                    value={formData.f_address_line1}
                                    onChange={(e) => setFormData({ ...formData, f_address_line1: e.target.value })}
                                    placeholder={t('customer.fields.addressLine1Placeholder')}
                                    size="small"
                                />
                            </Grid>

                            {/* Collapsible Address & Location Section */}
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="text"
                                    endIcon={addressExpanded ? <ExpandLess /> : <ExpandMore />}
                                    onClick={() => setAddressExpanded(!addressExpanded)}
                                    sx={{ justifyContent: 'space-between', pl: 0, pr: 0, textTransform: 'none', borderBottom: 1, borderColor: 'divider', borderRadius: 0 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationOn fontSize="small" color="action" />
                                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                                            {t('customer.fields.addressDetails')}
                                        </Typography>
                                        {(formData.f_city || formData.f_pincode || (formData.f_latitude && formData.f_longitude)) && (
                                            <Chip label={t('customer.fields.filled')} size="small" color="success" sx={{ ml: 1 }} />
                                        )}
                                    </Box>
                                </Button>
                                <Collapse in={addressExpanded} unmountOnExit>
                                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        {/* GPS Location */}
                                        <Grid item xs={12}>
                                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <GpsFixed fontSize="small" color="primary" />
                                                        <Typography variant="body2" fontWeight="bold">{t('customer.location.title')}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={gettingLocation ? <CircularProgress size={14} /> : <GpsFixed />}
                                                            onClick={handleDetectLocation}
                                                            disabled={gettingLocation}
                                                        >
                                                            {gettingLocation ? t('customer.actions.detecting') : t('customer.actions.detect')}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<MapIcon />}
                                                            onClick={() => setLocationPickerOpen(true)}
                                                        >
                                                            {t('customer.actions.pickOnMap')}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                {formData.f_latitude && formData.f_longitude ? (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                                                            <LocationOn fontSize="small" color="success" sx={{ mt: 0.2, flexShrink: 0 }} />
                                                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, lineHeight: 1.4 }}>
                                                                {locationLabel || `${parseFloat(formData.f_latitude).toFixed(5)}, ${parseFloat(formData.f_longitude).toFixed(5)}`}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Typography variant="caption" color="text.disabled">
                                                                {parseFloat(formData.f_latitude).toFixed(5)}, {parseFloat(formData.f_longitude).toFixed(5)}
                                                            </Typography>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                sx={{ py: 0, minHeight: 0, fontSize: '0.7rem' }}
                                                                onClick={() => { setFormData(p => ({ ...p, f_latitude: '', f_longitude: '' })); setLocationLabel(''); }}
                                                            >
                                                                {t('customer.actions.clear')}
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {t('customer.location.noLocation')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label={t('customer.fields.addressLine2')}
                                                value={formData.f_address_line2}
                                                onChange={(e) => setFormData({ ...formData, f_address_line2: e.target.value })}
                                                placeholder={t('customer.fields.addressLine2Placeholder')}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label={t('customer.fields.city')}
                                                value={formData.f_city}
                                                onChange={(e) => setFormData({ ...formData, f_city: e.target.value })}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label={t('customer.fields.state')}
                                                value={formData.f_state}
                                                onChange={(e) => setFormData({ ...formData, f_state: e.target.value })}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label={t('customer.fields.pincode')}
                                                value={formData.f_pincode}
                                                onChange={(e) => setFormData({ ...formData, f_pincode: e.target.value })}
                                                inputProps={{ maxLength: 6 }}
                                                size="small"
                                            />
                                        </Grid>
                                    </Grid>
                                </Collapse>
                            </Grid>

                            {/* Subscription Picker — only when creating new customer, business supports plans, and products exist */}
                            {!editingCustomer && showSubscriptions && products.some(p => !p.f_is_archived) && (
                                <Grid item xs={12}>
                                    <Divider sx={{ mb: 1 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Subscriptions fontSize="small" color="primary" />
                                        <Typography variant="body2" fontWeight="bold">
                                            {t('customer.subscriptions.addDuring', 'Add Subscriptions')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ({t('customer.subscriptions.optional', 'optional')})
                                        </Typography>
                                    </Box>
                                    {products.filter(p => !p.f_is_archived && p.f_is_available && [1, 2].includes(p.f_selling_mode)).map(p => {
                                        const sel = newCustSubs.find(s => s.productId === p.f_id);
                                        const billing = p.f_billing_type || p.product_billing_type;
                                        const billingLabel = billing === 'MonthlyFlatFee' ? 'Monthly' : billing === 'PerEachItem' ? 'Per Item' : billing || '';
                                        const price = p.current_price != null ? `₹${Number(p.current_price).toLocaleString('en-IN')}` : '';
                                        return (
                                            <Box
                                                key={p.f_id}
                                                sx={{
                                                    border: 1, borderColor: sel ? 'primary.main' : 'divider',
                                                    borderRadius: 1, mb: 1,
                                                    bgcolor: sel ? 'primary.50' : 'transparent',
                                                }}
                                            >
                                                {/* Top row: checkbox + name + price/billing + quantity controls */}
                                                <Box
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        p: 1, cursor: 'pointer',
                                                    }}
                                                    onClick={() => {
                                                        if (sel) {
                                                            setNewCustSubs(prev => prev.filter(s => s.productId !== p.f_id));
                                                        } else {
                                                            setNewCustSubs(prev => [...prev, { productId: p.f_id, quantity: 1, effectiveFrom: new Date().toISOString().split('T')[0] }]);
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                                        <Checkbox checked={!!sel} size="small" sx={{ p: 0 }} />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" noWrap>{p.f_name}</Typography>
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                {/* Which days it arrives, for a scheduled plan — without it
                                                                    "Curd" and "Sunday Curd" look like the same entry twice. */}
                                                                {[price, planDays(p) || billingLabel].filter(Boolean).join(' · ')}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {sel && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }} onClick={e => e.stopPropagation()}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setNewCustSubs(prev => prev.map(s => s.productId === p.f_id ? { ...s, quantity: Math.max(1, s.quantity - 1) } : s))}
                                                                sx={{ border: 1, borderColor: 'divider', width: 28, height: 28 }}
                                                            >
                                                                <Remove fontSize="small" />
                                                            </IconButton>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 24, textAlign: 'center' }}>
                                                                {sel.quantity}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setNewCustSubs(prev => prev.map(s => s.productId === p.f_id ? { ...s, quantity: s.quantity + 1 } : s))}
                                                                sx={{ border: 1, borderColor: 'divider', width: 28, height: 28 }}
                                                            >
                                                                <Add fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                                {/* Bottom row: effective from date (only when selected) */}
                                                {sel && (
                                                    <Box sx={{ px: 1, pb: 1 }} onClick={e => e.stopPropagation()}>
                                                        <TextField
                                                            type="date"
                                                            size="small"
                                                            fullWidth
                                                            label={t('customer.subscriptions.effectiveFrom', 'Start From')}
                                                            value={sel.effectiveFrom}
                                                            onChange={e => setNewCustSubs(prev => prev.map(s => s.productId === p.f_id ? { ...s, effectiveFrom: e.target.value } : s))}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 3, pt: 2, bgcolor: 'grey.50' }}>
                        <Button 
                            onClick={handleCloseDialog} 
                            disabled={loading}
                            size="large"
                        >
                            {t('customer.actions.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveCustomer}
                            disabled={loading || !formData.f_name || (formData.f_has_mobile !== false && !formData.f_mobile)}
                            startIcon={editingCustomer ? <Edit /> : <Add />}
                            size="large"
                        >
                            {loading ? t('customer.actions.saving') : (editingCustomer ? t('customer.actions.update') : t('customer.actions.create'))}
                        </Button>
                    </DialogActions>
                </Dialog>

            {/* Location Picker — rendered outside the form dialog to avoid z-index conflicts */}
            <LocationPicker
                open={locationPickerOpen}
                onClose={() => setLocationPickerOpen(false)}
                lat={formData.f_latitude}
                lng={formData.f_longitude}
                onConfirm={({ lat, lng, label }) => {
                    setFormData(p => ({ ...p, f_latitude: lat, f_longitude: lng }));
                    setLocationLabel(label);
                }}
            />

            {/* Customer Subscriptions Dialog */}
            <Dialog
                open={subscriptionsDialogOpen} 
                onClose={() => setSubscriptionsDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle sx={DIALOG_TITLE_SX}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Subscriptions />
                            <Typography variant="h6">
                                {t('customer.subscriptions.activeFor')} {selectedCustomer?.f_name}
                            </Typography>
                        </Box>
                        <IconButton 
                            onClick={() => setSubscriptionsDialogOpen(false)} 
                            sx={{ color: 'white' }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                
                <DialogContent sx={{ mt: 2 }}>
                    {/* Same plans view as the Plans page (shared component) */}
                    <CustomerPlansForCustomer
                        customer={selectedCustomer}
                        businessId={businessId}
                        products={products}
                        onChanged={() => {
                            if (selectedCustomer?.f_id) loadCustomerSubs(selectedCustomer.f_id);
                            // The customer card's Active / Paused chip comes from the tags, loaded once
                            // with the page — refresh them too, or a Resume still shows "Paused".
                            loadTags();
                        }}
                    />
                </DialogContent>
                
                <DialogActions>
                    <Button onClick={() => setSubscriptionsDialogOpen(false)}>
                        {t('customer.actions.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Add plans (shared picker) ── */}
            <AddPlansDialog
                open={subAddOpen}
                businessId={businessId}
                customer={subAddCustomer}
                products={products}
                takenIds={new Set((subsMap[subAddCustomer?.f_id] || []).map(i => i.subscription_log?.f_business_product_id))}
                onClose={() => setSubAddOpen(false)}
                onAdded={handleSubAdded}
            />

            {/* ── Customer Detail Drawer ── */}
            <Drawer
                anchor="bottom"
                open={Boolean(drawerCustomer)}
                onClose={handleCloseDrawer}
                PaperProps={{
                    sx: {
                        height: '92vh',
                        // Phone: full width. Desktop: the app's 720px column instead of the whole monitor.
                        width: '100%', maxWidth: APP_DIALOG_MAX_WIDTH, mx: 'auto',
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }
                }}
            >
                {drawerCustomer && (() => {
                    const lastInv = custLastInvoice[drawerCustomer.f_id];
                    const pmts = paymentsMap[drawerCustomer.f_id];
                    const pmtLoading = paymentsLoading[drawerCustomer.f_id];
                    const payColor = lastInv?.payment_status === 'paid' ? 'success' : lastInv?.payment_status === 'partial' ? 'warning' : 'error';
                    const cid = drawerCustomer.f_id;
                    const custInvoices = invoicesMap[cid];
                    const invVisible = invoicesVisible[cid] || 5;
                    const ordVisible = ordersVisible[cid] || 5;
                    const pmtVisible = paymentsVisible[cid] || 5;
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                            {/* ── Handle bar ── */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
                                <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
                            </Box>

                            {/* ── Sticky header ── */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                                <Avatar sx={{ bgcolor: getAvatarColor(drawerCustomer.f_name), width: 44, height: 44, fontWeight: 'bold', fontSize: 16, flexShrink: 0 }}>
                                    {getInitials(drawerCustomer.f_name)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography fontWeight="bold" noWrap>{drawerCustomer.f_name}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Phone sx={{ fontSize: 11, color: 'text.disabled' }} />
                                        <Typography variant="caption" color={drawerCustomer.f_has_mobile === false ? 'text.disabled' : 'text.secondary'} noWrap>
                                            {drawerCustomer.f_has_mobile === false ? 'No phone' : formatMobileForDisplay(drawerCustomer.f_mobile)}
                                        </Typography>
                                    </Box>
                                </Box>
                                {drawerCustomer.outstanding_balance != null && (
                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                        <Typography variant="body2" fontWeight="bold"
                                            color={drawerCustomer.outstanding_balance > 0 ? 'error.main' : 'success.main'}>
                                            {drawerCustomer.credit_balance > 0 ? `+₹${drawerCustomer.credit_balance.toFixed(2)}` : `₹${drawerCustomer.outstanding_balance.toFixed(2)}`}
                                        </Typography>
                                        <Typography variant="caption"
                                            color={drawerCustomer.outstanding_balance > 0 ? 'error.main' : 'success.main'}>
                                            {drawerCustomer.outstanding_balance > 0 ? t('customer.card.due') : drawerCustomer.credit_balance > 0 ? 'Overpaid' : t('customer.card.paidUp')}
                                        </Typography>
                                    </Box>
                                )}
                                <IconButton onClick={handleCloseDrawer}><Close /></IconButton>
                            </Box>

                            {/* ── Scrollable body ── */}
                            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>

                                {/* Quick actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                                    {drawerCustomer.f_has_mobile !== false && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                        <IconButton component="a" href={`tel:${drawerCustomer.f_mobile}`}
                                            sx={{ bgcolor: 'success.50', color: 'success.main', '&:hover': { bgcolor: 'success.100' } }}>
                                            <Call />
                                        </IconButton>
                                        <Typography variant="caption" color="text.secondary">{t('customer.actions.call')}</Typography>
                                    </Box>
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                        <IconButton onClick={() => handleOpenDialog(drawerCustomer)}
                                            sx={{ bgcolor: 'primary.50', color: 'primary.main', '&:hover': { bgcolor: 'primary.100' } }}>
                                            <Edit />
                                        </IconButton>
                                        <Typography variant="caption" color="text.secondary">{t('customer.actions.edit')}</Typography>
                                    </Box>
                                    {drawerCustomer.f_latitude && drawerCustomer.f_longitude ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                            <IconButton component="a"
                                                href={`https://maps.google.com/?q=${drawerCustomer.f_latitude},${drawerCustomer.f_longitude}`}
                                                target="_blank" rel="noopener noreferrer"
                                                sx={{ bgcolor: 'info.50', color: 'info.main', '&:hover': { bgcolor: 'info.100' } }}>
                                                <LocationOn />
                                            </IconButton>
                                            <Typography variant="caption" color="text.secondary">{t('customer.actions.map')}</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, opacity: 0.3 }}>
                                            <IconButton disabled><LocationOn /></IconButton>
                                            <Typography variant="caption" color="text.disabled">{t('customer.actions.map')}</Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                        <IconButton onClick={() => handleDeleteCustomer(drawerCustomer.f_id)}
                                            sx={{ bgcolor: 'error.50', color: 'error.main', '&:hover': { bgcolor: 'error.100' } }}>
                                            <Delete />
                                        </IconButton>
                                        <Typography variant="caption" color="text.secondary">{t('customer.actions.archive')}</Typography>
                                    </Box>
                                </Box>

                                {/* Key details */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 80, bgcolor: 'grey.50', borderRadius: 1, p: 1, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.disabled" display="block">{t('customer.card.area')}</Typography>
                                        <Typography variant="body2" fontWeight="medium" noWrap>{drawerCustomer.f_area_name || '—'}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 80, bgcolor: drawerCustomer.outstanding_balance > 0 ? 'error.50' : 'success.50', borderRadius: 1, p: 1, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.disabled" display="block">
                                            {drawerCustomer.credit_balance > 0 ? 'Overpaid' : t('customer.card.balance')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold"
                                            color={drawerCustomer.outstanding_balance > 0 ? 'error.main' : 'success.main'}>
                                            {drawerCustomer.credit_balance > 0
                                                ? `+₹${drawerCustomer.credit_balance.toFixed(2)}`
                                                : drawerCustomer.outstanding_balance != null
                                                    ? `₹${drawerCustomer.outstanding_balance.toFixed(2)}`
                                                    : '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 80, bgcolor: drawerCustomer.f_follow_up_date ? 'warning.50' : 'grey.50', borderRadius: 1, p: 1, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.disabled" display="block">{t('customer.card.followup')}</Typography>
                                        <Typography variant="body2" fontWeight="medium"
                                            color={drawerCustomer.f_follow_up_date ? 'warning.dark' : 'text.disabled'}>
                                            {drawerCustomer.f_follow_up_date
                                                ? new Date(drawerCustomer.f_follow_up_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                                                : '—'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Dues Share + Record Payment */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Button variant="contained" size="small" fullWidth startIcon={<CurrencyRupee />}
                                        onClick={() => handleOpenPayDialog(drawerCustomer)}>
                                        Record Payment
                                    </Button>
                                    {drawerCustomer.outstanding_balance != null && drawerCustomer.f_has_mobile !== false && (
                                        <Tooltip title="Share dues via WhatsApp">
                                            <Button variant="outlined" size="small" color="success"
                                                startIcon={<WhatsApp />}
                                                onClick={() => {
                                                    const phone = (drawerCustomer.f_mobile || '').replace(/\D/g, '');
                                                    const waPhone = phone.length === 10 ? `91${phone}` : phone;
                                                    const msg = `Dear ${drawerCustomer.f_name},\nYour outstanding balance is ₹${drawerCustomer.outstanding_balance.toFixed(2)}.\nKindly pay at your earliest convenience.\nThank you!`;
                                                    window.location.href = `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
                                                }}
                                                sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
                                            >
                                                Dues
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Box>

                                {/* Invoices section */}
                                {showSubscriptions && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>Monthly Bills</Typography>
                                    {custInvoices === undefined ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <CircularProgress size={14} />
                                            <Typography variant="caption" color="text.secondary">Loading bills…</Typography>
                                        </Box>
                                    ) : custInvoices.length === 0 ? (
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>No bills yet</Typography>
                                    ) : (
                                        <Box sx={{ mt: 0.5 }}>
                                            {custInvoices.slice(0, invVisible).map(inv => {
                                                const iPayColor = inv.payment_status === 'Paid' ? 'success' : inv.payment_status === 'Partial' ? 'warning' : 'error';
                                                return (
                                                    <Box key={inv.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                                {inv.invoice_period_display} · #{inv.f_invoice_number}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    ₹{parseFloat(inv.f_total_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                                </Typography>
                                                                <Chip size="small" label={inv.payment_status === 'Paid' ? 'Paid' : inv.payment_status === 'Partial' ? 'Part Paid' : 'Unpaid'} color={iPayColor} sx={{ height: 16, fontSize: '0.65rem' }} />
                                                            </Box>
                                                        </Box>
                                                        <ShareButtons
                                                            size="sm"
                                                            label={false}
                                                            phone={drawerCustomer.f_mobile}
                                                            noPhone={drawerCustomer.f_has_mobile === false}
                                                            text={buildInvoiceShareText(inv, drawerCustomer.f_name)}
                                                            getShareUrl={async () => {
                                                                const res = await invoiceAPI.getInvoiceShareUrl(businessId, inv.f_id);
                                                                return res.data?.share_url;
                                                            }}
                                                            onView={() => setViewInvoiceId(inv.f_id)}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                            {custInvoices.length > invVisible && (
                                                <Button size="small" fullWidth sx={{ mt: 0.75, textTransform: 'none' }}
                                                    onClick={() => setInvoicesVisible(prev => ({ ...prev, [cid]: invVisible + 5 }))}>
                                                    Show more ({custInvoices.length - invVisible} more)
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                )}

                                {/* Payment History */}
                                {(pmts || pmtLoading) && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>Payment History</Typography>
                                        {pmtLoading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <CircularProgress size={14} />
                                                <Typography variant="caption" color="text.secondary">Loading…</Typography>
                                            </Box>
                                        ) : pmts && pmts.length === 0 ? (
                                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>No payments yet</Typography>
                                        ) : (
                                            <Box sx={{ mt: 0.5 }}>
                                                {(pmts || []).slice(0, pmtVisible).map(p => (
                                                    <Box key={p.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                ₹{parseFloat(p.f_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                                {parseFloat(p.f_discount) > 0 && (
                                                                    <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                                                                        +₹{parseFloat(p.f_discount).toLocaleString('en-IN', { maximumFractionDigits: 0 })} disc.
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(p.f_time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })} · {p.f_payment_method}
                                                            </Typography>
                                                        </Box>
                                                        <Tooltip title="Share receipt">
                                                            <IconButton size="small" color="primary" onClick={() => openShareSheet({
                                                                payment_id: p.f_id,
                                                                customer_name: drawerCustomer.f_name,
                                                                customer_mobile: drawerCustomer.f_mobile,
                                                                amount: parseFloat(p.f_amount),
                                                                discount: parseFloat(p.f_discount) || 0,
                                                                method: p.f_payment_method,
                                                                time: new Date(p.f_time),
                                                                new_balance: null,
                                                            })}>
                                                                <ShareIcon fontSize="inherit" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit payment">
                                                            <IconButton size="small" onClick={() => handleOpenEditPayDialog(drawerCustomer, p)}>
                                                                <Edit fontSize="inherit" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete payment">
                                                            <IconButton size="small" color="error" onClick={() => { setDeletePaymentTarget({ paymentId: p.f_id, customerId: drawerCustomer.f_id }); setDeletePaymentOpen(true); }}>
                                                                <Delete fontSize="inherit" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ))}
                                                {(pmts || []).length > pmtVisible && (
                                                    <Button size="small" fullWidth sx={{ mt: 0.75, textTransform: 'none' }}
                                                        onClick={() => setPaymentsVisible(prev => ({ ...prev, [cid]: pmtVisible + 5 }))}>
                                                        Show more ({pmts.length - pmtVisible} more)
                                                    </Button>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Subscriptions */}
                                {showSubscriptions && (<>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>{t('customer.subscriptions.title')}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Tooltip title="Add plan">
                                            <IconButton size="small" color="primary" onClick={() => handleOpenSubAdd(drawerCustomer)}>
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {subsMap[drawerCustomer.f_id]?.length > 0 && (
                                            <Tooltip title="Manage plans">
                                                <IconButton size="small" onClick={() => handleManageSubscriptions(drawerCustomer)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                                {subsMap[drawerCustomer.f_id] === undefined ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary">{t('customer.card.loading')}</Typography>
                                    </Box>
                                ) : subsMap[drawerCustomer.f_id].length === 0 ? (
                                    <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5, fontStyle: 'italic' }}>{t('customer.subscriptions.none')}</Typography>
                                ) : (
                                    <Box sx={{ mb: 1.5 }}>
                                        {subsMap[drawerCustomer.f_id].map((item) => {
                                            const isPaused = item.subscription_log.f_action === 'Suspend';
                                            return (
                                                <Box key={item.subscription_log.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                                    <Avatar variant="rounded" sx={{ bgcolor: isPaused ? 'warning.light' : 'primary.light', color: isPaused ? 'warning.dark' : 'primary.dark', width: 36, height: 36, fontSize: 14 }}>
                                                        {(item.product_details?.f_name || 'P').charAt(0)}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Typography variant="body2" fontWeight="medium" noWrap>
                                                                {item.product_details?.f_name || item.subscription_details?.f_name || 'Product'}
                                                            </Typography>
                                                            {isPaused && <Chip label="Paused" size="small" color="warning" sx={{ height: 16, fontSize: 10 }} />}
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.subscription_details?.f_subscription_period} · {item.subscription_log.f_quantity} unit{item.subscription_log.f_quantity !== 1 ? 's' : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                                </>)}

                                {/* Orders */}
                                {showOrders && (
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                    <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>Orders</Typography>
                                    {ordersMap[drawerCustomer.f_id] === undefined ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                                            <CircularProgress size={14} />
                                            <Typography variant="caption" color="text.secondary">Loading orders…</Typography>
                                        </Box>
                                    ) : ordersMap[drawerCustomer.f_id].length === 0 ? (
                                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 0.5 }}>No orders yet</Typography>
                                    ) : (
                                        <Box sx={{ mt: 0.5 }}>
                                            {ordersMap[drawerCustomer.f_id].slice(0, ordVisible).map(order => {
                                                const statusColor = { created: 'default', accepted: 'info', delivered: 'success', cancelled: 'error' }[order.f_status] || 'default';
                                                const payColor = { due: 'warning', paid_directly: 'success', invoice: 'info', pay_later: 'default', void: 'error' }[order.f_payment_status] || 'default';
                                                const items = (order.items || []).map(i => i.product_name || i.f_business_product_id).join(', ');
                                                return (
                                                    <Box key={order.f_id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                #{order.f_order_number ?? order.f_id} &nbsp;₹{parseFloat(order.f_total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {new Date(order.f_updated_at || order.f_inserted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                                                                </Typography>
                                                                {drawerCustomer.f_has_mobile !== false && (
                                                                <Tooltip title="Share order via WhatsApp">
                                                                    <IconButton size="small" color="success" onClick={async () => {
                                                                        const phone = (drawerCustomer.f_mobile || '').replace(/\D/g, '');
                                                                        const waPhone = phone.length === 10 ? `91${phone}` : phone;
                                                                        const waMsg = `Dear ${drawerCustomer.f_name},\nOrder #${order.f_order_number ?? order.f_id}\nAmount: ₹${parseFloat(order.f_total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}\nStatus: ${order.f_status}${items ? `\nItems: ${items}` : ''}`;
                                                                        try {
                                                                            const res = await orderAPI.getOrderShareUrl(businessId, order.f_id);
                                                                            const link = res.data?.share_url;
                                                                            window.location.href = `https://wa.me/${waPhone}?text=${encodeURIComponent(link ? `${waMsg}\n${link}` : waMsg)}`;
                                                                        } catch (_) {
                                                                            window.location.href = `https://wa.me/${waPhone}?text=${encodeURIComponent(waMsg)}`;
                                                                        }
                                                                    }}>
                                                                        <WhatsApp fontSize="inherit" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {items ? <Typography variant="caption" color="text.secondary" noWrap>{items}</Typography> : null}
                                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4 }}>
                                                            <Chip label={order.f_status} size="small" color={statusColor} sx={{ height: 18, fontSize: '0.65rem' }} />
                                                            <Chip label={order.f_payment_status?.replace('_', ' ')} size="small" color={payColor} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                            {ordersMap[drawerCustomer.f_id].length > ordVisible && (
                                                <Button size="small" fullWidth sx={{ mt: 0.75, textTransform: 'none' }}
                                                    onClick={() => setOrdersVisible(prev => ({ ...prev, [cid]: ordVisible + 5 }))}>
                                                    Show more ({ordersMap[drawerCustomer.f_id].length - ordVisible} more)
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                )}

                                {/* Notes */}
                                <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 1 }}>{t('customer.notes.title')}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mt: 0.5 }}
                                    onClick={handleToggleDrawerNotes}>
                                    <Box sx={{ flex: 1, mr: 1 }}>
                                        {drawerCustomer.f_note ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                "{drawerCustomer.f_note}"
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>{t('customer.notes.noNotes')}</Typography>
                                        )}
                                        {drawerCustomer.f_follow_up_date && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                <FollowUpIcon sx={{ fontSize: 13, color: 'warning.main' }} />
                                                <Typography variant="caption" color="warning.main" fontWeight="bold">
                                                    {t('customer.notes.followupPrefix')} {new Date(drawerCustomer.f_follow_up_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {drawerNotesOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </Box>
                                <Collapse in={drawerNotesOpen} unmountOnExit>
                                    <Box sx={{ mt: 1 }}>
                                        {(notesMap[drawerCustomer.f_id] || []).map((n) => (
                                            <Box key={n.f_id} sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, mb: 0.75, borderLeft: 3, borderColor: 'primary.light' }}>
                                                <Typography variant="body2">{n.f_note}</Typography>
                                                <Typography variant="caption" color="text.disabled">
                                                    {new Date(n.f_inserted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {(notesMap[drawerCustomer.f_id] || []).length === 0 && (
                                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>{t('customer.notes.noPrevious')}</Typography>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                            <TextField
                                                size="small" fullWidth multiline minRows={2}
                                                placeholder={t('customer.notes.addPlaceholder')}
                                                value={newNoteText}
                                                onChange={(e) => setNewNoteText(e.target.value)}
                                                inputProps={{ maxLength: 2000 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    size="small" type="date" label="Follow-up date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={newFollowUpDate}
                                                    onChange={(e) => setNewFollowUpDate(e.target.value)}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Button
                                                    variant="contained" size="small"
                                                    endIcon={noteLoading ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                                                    onClick={() => handleAddNote(drawerCustomer.f_id)}
                                                    disabled={(!newNoteText.trim() && !newFollowUpDate) || noteLoading}
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Collapse>

                                {/* Address */}
                                {(drawerCustomer.f_city || drawerCustomer.f_address_line1) && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                        <LocationOn sx={{ fontSize: 14, color: 'text.disabled', mt: 0.2, flexShrink: 0 }} />
                                        <Typography variant="caption" color="text.disabled">
                                            {[drawerCustomer.f_address_line1, drawerCustomer.f_city, drawerCustomer.f_state, drawerCustomer.f_pincode].filter(Boolean).join(', ')}
                                        </Typography>
                                    </Box>
                                )}

                            </Box>
                        </Box>
                    );
                })()}
            </Drawer>

            {/* Shared invoice viewer — same server-rendered page that gets shared */}
            <InvoiceViewDialog businessId={businessId} invoiceId={viewInvoiceId} onClose={() => setViewInvoiceId(null)} />

            {/* ── Record / Edit Payment Dialog ── */}
            <Dialog open={payDialogOpen} onClose={() => !payLoading && setPayDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {payEditId ? 'Edit Payment' : 'Record Payment'}
                    <IconButton size="small" onClick={() => !payLoading && setPayDialogOpen(false)}><Close /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {payDialogCustomer && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">{payDialogCustomer.f_name}</Typography>
                            {!payEditId && payDialogCustomer.outstanding_balance != null && (
                                <Typography variant="caption" color={payDialogCustomer.outstanding_balance > 0 ? 'error.main' : 'success.main'}>
                                    Balance due: ₹{parseFloat(payDialogCustomer.outstanding_balance).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <TextField
                        label="Amount Paid (₹)" type="number" size="small" fullWidth autoFocus
                        value={payAmount}
                        onChange={e => setPayAmount(e.target.value)}
                        inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Discount / Adjustment (₹)" type="number" size="small" fullWidth
                        value={payDiscount}
                        onChange={e => setPayDiscount(e.target.value)}
                        inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
                        helperText="Optional — amount forgiven or adjusted (reduces balance without cash)"
                        sx={{ mb: 2 }}
                    />
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Payment Method</InputLabel>
                        <Select label="Payment Method" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="UPI">UPI</MenuItem>
                            <MenuItem value="BankTransfer">Bank Transfer</MenuItem>
                            <MenuItem value="Cheque">Cheque</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Date" type="date" size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={payDate}
                        onChange={e => setPayDate(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Notes (optional)" size="small" fullWidth multiline minRows={2}
                        value={payNotes}
                        onChange={e => setPayNotes(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPayDialogOpen(false)} disabled={payLoading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSavePayment}
                        disabled={payLoading || (parseFloat(payAmount) < 0) || (parseFloat(payAmount || 0) === 0 && parseFloat(payDiscount || 0) <= 0)}
                        startIcon={payLoading ? <CircularProgress size={16} color="inherit" /> : <CurrencyRupee />}
                    >
                        {payLoading ? 'Saving…' : payEditId ? 'Save Changes' : parseFloat(payAmount || 0) > 0 ? `Record ₹${parseFloat(payAmount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : 'Apply Discount'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Payment Confirmation Dialog ── */}
            <Dialog open={deletePaymentOpen} onClose={() => setDeletePaymentOpen(false)} maxWidth="xs">
                <DialogTitle>Delete Payment?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">This will permanently remove the payment record. The customer's balance will be recalculated.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeletePaymentOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeletePayment}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* ── Share Receipt Dialog ── */}
            <Dialog
                open={!!sharePayment}
                onClose={() => { setSharePayment(null); setReceiptUrl(null); }}
                fullWidth maxWidth="xs"
            >
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ShareIcon fontSize="small" />
                        Share Receipt
                    </Box>
                    <IconButton size="small" onClick={() => { setSharePayment(null); setReceiptUrl(null); }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    {sharePayment && (
                        <>
                            {/* Receipt preview */}
                            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                                <Typography variant="body2" fontWeight="bold">{sharePayment.customer_name}</Typography>
                                <Typography variant="body2">
                                    Paid ₹{Number(sharePayment.amount).toLocaleString('en-IN')}
                                    {sharePayment.discount > 0 ? ` + ₹${Number(sharePayment.discount).toLocaleString('en-IN')} disc.` : ''}
                                    {' · '}{sharePayment.method}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(sharePayment.time).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                                {sharePayment.new_balance !== null && (
                                    <Typography variant="caption" display="block" color={Number(sharePayment.new_balance) <= 0 ? 'success.main' : 'warning.main'} fontWeight="bold">
                                        {Number(sharePayment.new_balance) <= 0 ? 'Balance Cleared' : `Remaining: ₹${Number(sharePayment.new_balance).toLocaleString('en-IN')}`}
                                    </Typography>
                                )}
                            </Box>
                            {/* Receipt PDF status */}
                            {receiptLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <CircularProgress size={14} />
                                    <Typography variant="caption" color="text.secondary">Preparing receipt PDF…</Typography>
                                </Box>
                            )}
                            {receiptUrl && !receiptLoading && (
                                <Chip size="small" icon={<CheckCircleOutline />} label="Receipt PDF ready" color="success" sx={{ mb: 1.5 }} />
                            )}
                            {/* Share buttons */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Button
                                    fullWidth variant="contained"
                                    startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <WhatsApp />}
                                    disabled={receiptLoading}
                                    onClick={() => handleReceiptShareWhatsApp(sharePayment, false)}
                                    sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe5d' }, textTransform: 'none' }}
                                >
                                    WhatsApp
                                </Button>
                                <Button
                                    fullWidth variant="contained"
                                    startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <WhatsApp />}
                                    disabled={receiptLoading}
                                    onClick={() => handleReceiptShareWhatsApp(sharePayment, true)}
                                    sx={{ bgcolor: '#128C7E', '&:hover': { bgcolor: '#075E54' }, textTransform: 'none' }}
                                >
                                    WhatsApp Business
                                </Button>
                                <Button
                                    fullWidth variant="contained"
                                    startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <Sms />}
                                    disabled={receiptLoading}
                                    onClick={() => handleReceiptShareSms(sharePayment)}
                                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, textTransform: 'none' }}
                                >
                                    SMS
                                </Button>
                                <Button
                                    fullWidth variant="text" color="inherit"
                                    onClick={() => { setSharePayment(null); setReceiptUrl(null); }}
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Skip
                                </Button>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Archive Customer Confirmation Dialog ── */}
            <Dialog open={archiveConfirmOpen} onClose={() => { if (!archiveLoading) { setArchiveConfirmOpen(false); setArchiveTarget(null); } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Archive Customer?</DialogTitle>
                <DialogContent>
                    {(() => {
                        const activeSubs = (subsMap[archiveTarget] || []).filter(s =>
                            s.status === 'Active' || s.status === 'Suspended'
                        );
                        return (
                            <Box>
                                {activeSubs.length > 0 && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        This customer has <strong>{activeSubs.length} active plan(s)</strong> that will be ended as of the previous month's end date.
                                    </Alert>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    This will:
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                                    {activeSubs.length > 0 && (
                                        <Typography component="li" variant="body2">End all active plans</Typography>
                                    )}
                                    <Typography component="li" variant="body2">Remove all deliveries and adjustments from the current working month</Typography>
                                    <Typography component="li" variant="body2">Payments will be retained</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                    The customer can be restored later from the archived list.
                                </Typography>
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setArchiveConfirmOpen(false); setArchiveTarget(null); }} disabled={archiveLoading}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmArchiveCustomer} disabled={archiveLoading}>
                        {archiveLoading ? 'Archiving…' : 'Archive'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Contact Import Dialog */}
            <Dialog open={bulkDialogOpen} onClose={() => { if (!bulkImporting) setBulkDialogOpen(false); }} maxWidth="sm" fullWidth>
                <DialogTitle sx={DIALOG_TITLE_SX}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ContactsIcon />
                            <Typography variant="h6">{t('customer.import.title')}</Typography>
                        </Box>
                        <IconButton onClick={() => { if (!bulkImporting) setBulkDialogOpen(false); }} sx={{ color: 'white' }} disabled={bulkImporting}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {bulkImporting && <LinearProgress />}
                    <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('customer.import.selectedOf', { selected: bulkContacts.filter(c => c.selected).length, total: bulkContacts.length })}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => {
                                const allSelected = bulkContacts.every(c => c.selected);
                                setBulkContacts(bulkContacts.map(c => ({ ...c, selected: !allSelected })));
                            }}
                            disabled={bulkImporting}
                        >
                            {bulkContacts.every(c => c.selected) ? t('customer.actions.deselectAll') : t('customer.actions.selectAll')}
                        </Button>
                    </Box>
                    <Divider />
                    <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {bulkContacts.map((contact, idx) => (
                            <ListItem key={idx} disablePadding sx={{ px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Checkbox
                                    checked={contact.selected}
                                    onChange={(e) => {
                                        const updated = [...bulkContacts];
                                        updated[idx] = { ...updated[idx], selected: e.target.checked };
                                        setBulkContacts(updated);
                                    }}
                                    disabled={bulkImporting || contact.status === 'success'}
                                    size="small"
                                />
                                <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        label={t('customer.import.name')}
                                        value={contact.name}
                                        onChange={(e) => {
                                            const updated = [...bulkContacts];
                                            updated[idx] = { ...updated[idx], name: e.target.value };
                                            setBulkContacts(updated);
                                        }}
                                        disabled={bulkImporting || contact.status === 'success'}
                                        sx={{ flex: 1 }}
                                        error={contact.status === 'error'}
                                    />
                                    <TextField
                                        size="small"
                                        label={t('customer.import.mobile')}
                                        value={contact.mobile}
                                        onChange={(e) => {
                                            const updated = [...bulkContacts];
                                            updated[idx] = { ...updated[idx], mobile: e.target.value.replace(/\D/g, '').slice(-10) };
                                            setBulkContacts(updated);
                                        }}
                                        disabled={bulkImporting || contact.status === 'success'}
                                        inputProps={{ maxLength: 10 }}
                                        sx={{ width: 130 }}
                                        error={contact.status === 'error'}
                                    />
                                    <Box sx={{ width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {contact.status === 'success' && <CheckCircle color="success" fontSize="small" />}
                                        {contact.status === 'error' && (
                                            <Tooltip title={contact.error}>
                                                <Warning color="error" fontSize="small" />
                                            </Tooltip>
                                        )}
                                        {contact.status === 'importing' && <CircularProgress size={16} />}
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                    {bulkDone && (
                        <Box sx={{ px: 2, py: 1 }}>
                            <Alert severity={bulkContacts.filter(c => c.selected && c.status === 'error').length === 0 ? 'success' : 'warning'}>
                                {t('customer.import.imported')}: {bulkContacts.filter(c => c.status === 'success').length} &nbsp;|&nbsp;
                                {t('customer.import.failed')}: {bulkContacts.filter(c => c.selected && c.status === 'error').length} &nbsp;|&nbsp;
                                {t('customer.import.skipped')}: {bulkContacts.filter(c => !c.selected).length}
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkImporting}>{t('customer.actions.close')}</Button>
                    {!bulkDone && (
                        <Button
                            variant="contained"
                            onClick={handleBulkImport}
                            disabled={bulkImporting || bulkContacts.filter(c => c.selected).length === 0}
                            startIcon={bulkImporting ? <CircularProgress size={16} /> : <ContactsIcon />}
                        >
                            {bulkImporting ? t('customer.actions.importing') : `${t('customer.actions.import')} ${bulkContacts.filter(c => c.selected).length} Contacts`}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* ── Snackbar for archive feedback ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 8000 : 4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px) !important' }}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            </>
        );
    };

export default CustomerManager;